import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";

export function formatLocationLabel(loc) {
  return loc?.label || loc?.city || "";
}

export default function LocationSearchInput({
  label,
  value,
  onChange,
  onSelect,
  testId,
  country = "CA",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const sessionTokenRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const search = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      setHint(null);
      setUsingFallback(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/locations/search`, {
          params: {
            q: q.trim(),
            country,
            session_token: sessionTokenRef.current,
            limit: 8,
          },
        });
        const list = r.data?.results || r.data?.suggestions || r.data?.locations || [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setHint(r.data?.hint || r.data?.integration_hint || null);
        setUsingFallback(!!r.data?.using_fallback);
      } catch (e) {
        setSuggestions([]);
        setOpen(false);
        setHint("Could not load locations — check that the backend is running.");
        setUsingFallback(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const selectSuggestion = async (suggestion) => {
    let loc = suggestion;
    const resolveId = loc.place_id || loc.location_id;
    const needsResolve = resolveId && (loc.lat == null || loc.lng == null);
    if (needsResolve) {
      try {
        const r = await axios.get(`${API}/locations/resolve`, {
          params: {
            place_id: resolveId,
            session_token: sessionTokenRef.current,
          },
        });
        loc = r.data?.location || r.data || loc;
      } catch (e) {
        /* proceed with partial location */
      }
    }
    onSelect(loc);
    setOpen(false);
    setSuggestions([]);
  };

  const suggestionKey = (s, i) =>
    s.location_id || s.place_id || s.label || s.city || `suggestion-${i}`;

  const metaParts = (s) =>
    [s.airport_code, s.country_code, s.provider].filter(Boolean);

  return (
    <div ref={wrapRef} className="relative">
      <label className="text-xs text-[var(--zc-text-3)]">{label}</label>
      <input
        className="zc-input h-9 px-3 w-full text-sm mt-1"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => value.length >= 2 && suggestions.length > 0 && setOpen(true)}
        placeholder="Los Angeles, LAX, or street address"
        data-testid={testId}
        autoComplete="off"
      />
      {loading && (
        <div className="zc-meta mt-0.5">Searching locations…</div>
      )}
      {!loading && (hint || usingFallback) && (
        <div className="zc-hint text-[var(--zc-text-2)] mt-1" data-testid={`${testId}-hint`}>
          {hint}
          {usingFallback ? " Showing built-in cities — live search may be unavailable." : ""}
        </div>
      )}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-auto zc-card border border-[var(--zc-border)] shadow-lg text-sm"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li key={suggestionKey(s, i)}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-[var(--zc-secondary-soft)]"
                onClick={() => selectSuggestion(s)}
              >
                <div className="font-medium text-[var(--zc-text)]">
                  {formatLocationLabel(s)}
                </div>
                {metaParts(s).length > 0 && (
                  <div className="zc-meta">{metaParts(s).join(" · ")}</div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
